import { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import {
  RadioButton,
  RadioGroup,
} from '@/lib/base/radio-buttons/radio-buttons';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import type { AssocGroup, AssocReviewItem } from './merge-association-groups';
import {
  finalAssociationTargets,
  useVisibleAssociationGroups,
} from './merge-association-groups';
import { useMergeWizard } from './MergeWizardContext';

interface AssocDismissButtonProps {
  id: string;
  dismissed: boolean;
  onToggle: (id: string) => void;
}

function AssocDismissButton({
  id,
  dismissed,
  onToggle,
}: AssocDismissButtonProps) {
  const handleClick = useCallback(() => {
    onToggle(id);
  }, [id, onToggle]);

  return (
    <Button color='link-gray' size='sm' onClick={handleClick}>
      {dismissed ? 'Restore' : 'Dismiss'}
    </Button>
  );
}

interface AssocToOnePickerProps {
  assocType: string;
  label: string;
  canonicalTarget: string;
  sourceTarget: string;
  selectedTarget: string;
  orgId: number;
  onSelect: (assocType: string, targetId: string) => void;
}

/**
 * Radio pick-one for a `*_to_one` association conflict (e.g. each record works
 * at a different company). The merged record keeps exactly one target.
 */
function AssocToOnePicker({
  assocType,
  label,
  canonicalTarget,
  sourceTarget,
  selectedTarget,
  orgId,
  onSelect,
}: AssocToOnePickerProps) {
  const handleChange = useCallback(
    (value: string) => onSelect(assocType, value),
    [onSelect, assocType]
  );

  return (
    <div
      className='mb-4 rounded-lg border border-[var(--color-border-secondary)] p-3'
      data-testid={`merge-assoc-group-${assocType}`}
    >
      <h3 className='mb-1 text-body-small font-medium text-[var(--color-text-secondary)]'>
        {label}
      </h3>
      <p className='mb-2 text-body-small text-[var(--color-text-tertiary)]'>
        Keep one — the merged record can have only one.
      </p>
      <RadioGroup
        aria-label={`Choose ${label}`}
        value={selectedTarget}
        onChange={handleChange}
        className='gap-2'
      >
        <RadioButton
          value={canonicalTarget}
          label={
            <EntityBadgeById
              entityId={canonicalTarget}
              orgId={orgId}
              variant='xs'
              avatar
              interactive={false}
            />
          }
        />
        <RadioButton
          value={sourceTarget}
          label={
            <EntityBadgeById
              entityId={sourceTarget}
              orgId={orgId}
              variant='xs'
              avatar
              interactive={false}
            />
          }
        />
      </RadioGroup>
    </div>
  );
}

export interface AssocConflictPickersProps {
  conflicts: AssocGroup[];
  orgId: number;
}

/** The `*_to_one` association pick-ones, for the Step 2 Conflicts section. */
export function AssocConflictPickers({
  conflicts,
  orgId,
}: AssocConflictPickersProps) {
  const { associationChoices, setAssociationChoice } = useMergeWizard();

  const handleSelect = useCallback(
    (assocType: string, targetId: string) => {
      setAssociationChoice(assocType, targetId);
    },
    [setAssociationChoice]
  );

  return (
    <>
      {conflicts.map(group =>
        group.canonicalTarget && group.sourceTarget ? (
          <AssocToOnePicker
            key={group.assocType}
            assocType={group.assocType}
            label={group.label}
            canonicalTarget={group.canonicalTarget}
            sourceTarget={group.sourceTarget}
            selectedTarget={
              associationChoices[group.assocType] ?? group.canonicalTarget
            }
            orgId={orgId}
            onSelect={handleSelect}
          />
        ) : null
      )}
    </>
  );
}

export interface AssocAutoResolvedListProps {
  items: AssocReviewItem[];
  orgId: number;
}

/** One-sided associations that transfer automatically; each can be dismissed. */
export function AssocAutoResolvedList({
  items,
  orgId,
}: AssocAutoResolvedListProps) {
  const {
    dismissedAssociationIds,
    addDismissedAssociation,
    removeDismissedAssociation,
  } = useMergeWizard();

  const handleToggle = useCallback(
    (id: string) => {
      if (dismissedAssociationIds.has(id)) {
        removeDismissedAssociation(id);
      } else {
        addDismissedAssociation(id);
      }
    },
    [
      dismissedAssociationIds,
      addDismissedAssociation,
      removeDismissedAssociation,
    ]
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <ul className='flex flex-col gap-1'>
      {items.map(item => (
        <li
          key={item.id}
          className='flex items-center justify-between gap-2 rounded-md border border-[var(--color-border-secondary)] px-3 py-2'
        >
          <span
            data-testid={`merge-assoc-target-${item.id}`}
            className={cx(
              'flex min-w-0 items-center gap-2 text-body-small',
              dismissedAssociationIds.has(item.id) && 'line-through opacity-60'
            )}
          >
            <span className='text-[var(--color-text-tertiary)]'>
              {item.label}
            </span>
            <EntityBadgeById
              entityId={item.targetId}
              orgId={orgId}
              variant='xs'
              avatar
              interactive={false}
            />
          </span>
          <AssocDismissButton
            id={item.id}
            dismissed={dismissedAssociationIds.has(item.id)}
            onToggle={handleToggle}
          />
        </li>
      ))}
    </ul>
  );
}

export interface AssocMatchingListProps {
  items: AssocReviewItem[];
  orgId: number;
}

/** Associations present on both records — shown read-only. */
export function AssocMatchingList({ items, orgId }: AssocMatchingListProps) {
  if (items.length === 0) {
    return null;
  }
  return (
    <ul className='flex flex-col gap-1'>
      {items.map(item => (
        <li key={item.id} className='flex items-center gap-2 text-body-small'>
          <span className='text-[var(--color-text-tertiary)]'>
            {item.label}
          </span>
          <EntityBadgeById
            entityId={item.targetId}
            orgId={orgId}
            variant='xs'
            avatar
            interactive={false}
          />
        </li>
      ))}
    </ul>
  );
}

export interface MergeAssociationsSummaryProps {
  canonicalObject: CrmObject | undefined;
  sourceObject: CrmObject | undefined;
  orgId: number;
}

/**
 * Read-only association summary for Step 3: shows the FINAL targets the merged
 * record will keep, reflecting the choices made in Step 2. No inputs.
 */
export function MergeAssociationsSummary({
  canonicalObject,
  sourceObject,
  orgId,
}: MergeAssociationsSummaryProps) {
  const { groups, isLoading } = useVisibleAssociationGroups(
    canonicalObject,
    sourceObject,
    orgId
  );
  const { associationChoices, dismissedAssociationIds } = useMergeWizard();

  const resolved = useMemo(
    () =>
      groups
        .map(group => ({
          group,
          targets: finalAssociationTargets(
            group,
            associationChoices,
            dismissedAssociationIds
          ),
        }))
        .filter(entry => entry.targets.length > 0),
    [groups, associationChoices, dismissedAssociationIds]
  );

  if (isLoading) {
    return (
      <div
        className='flex flex-col gap-2'
        data-testid='merge-assoc-summary-loading'
      >
        <Skeleton className='h-9 w-full rounded-md' />
      </div>
    );
  }

  if (resolved.length === 0) {
    return (
      <p className='text-body-small text-[var(--color-text-tertiary)]'>
        No associations.
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {resolved.map(({ group, targets }) => (
        <section
          key={group.assocType}
          data-testid={`merge-assoc-summary-${group.assocType}`}
        >
          <h3 className='mb-2 text-body-small font-semibold text-[var(--color-text-secondary)]'>
            {group.label}
          </h3>
          <div className='flex flex-wrap gap-2'>
            {targets.map(targetId => (
              <EntityBadgeById
                key={targetId}
                entityId={targetId}
                orgId={orgId}
                variant='xs'
                avatar
                interactive={false}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
